import { makeStyles } from "@material-ui/core";
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PlMultiSelect,
  PL_POS_C,
  PL_NEG_C,
  PL_FONT,
  PL_TH_BG,
  PL_TH_TEXT,
  PL_TH_BORDER,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import {
  getCmActualsVsForecastByProjAPI,
  getVersionsAPI,
} from "../../../../api/reports-api";
import { getApiFilterKey, formatActualsThroughDate, formatActualsThroughFromLabel, headerActualsDate } from "./plTimeFilterUtils";
import { toReportApiFilters } from "../filterRelationshipUtils";
import {
  applyNumFilter,
  isNumFilterActive,
  calcOpDropdownPos,
  renderStandardNumFilter,
  renderStandardTextFilter,
} from "./plTableFilterUtils";
import { OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";
import {
  calcFilterPos,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "../waterfall-eac/filterPopoverUtils";
import Loading from "../../../../common-components/Loading";
import { VERSION_OPTIONS_FALLBACK } from "../../versionOrder";

import { TH_BG, TH_BG_BAND, ACCENT_COLOR } from "../../reportTableConfig";

const HAIRLINE = "#E2E8F0";
const HEADER_TOP = PL_TH_BG;
const HEADER_ROW = PL_TH_BG;
const HEADER_LABEL = PL_TH_TEXT;
const FORECAST_C = "#2563EB";
const ACTUALS_C = "#16A34A";
const TEXT_MUTED = "#6B7280";
const TEXT_PRIMARY = "#111827";
const ROW_STRIPE = "#F7FAFC";
const TOTAL_BG = "#F0FFF4";
const ACCENT = ACCENT_COLOR;
const ACCENT2 = "#EEF2FF";
/* Fixed height of the group header row (Live AC / Comparison / Variance) — the
   measure header row below it sticks starting at this offset, matching EAC
   Variance View's two-row sticky header (thFixedGroup/thScrollGroup top:0,
   thFixedCol/thScrollCol top:GH). Without this, both rows fight for the same
   top:0 sticky slot and visibly overlap/jitter while scrolling. */
const GROUP_ROW_H = 44;

/** Wider name column when few metric cols are showing; tighter when many. */
function nameColMaxWidth(metricColCount) {
  if (metricColCount <= 3) return 280;
  if (metricColCount <= 6) return 240;
  if (metricColCount <= 12) return 200;
  return 160;
}

/* ══════════════════════════════════════════════════════════════ */
/*  Static config                                                  */
/* ══════════════════════════════════════════════════════════════ */
/* Compare-version fallback (Live EAC excluded — it is the baseline column) */
const VERSION_FALLBACK = VERSION_OPTIONS_FALLBACK.filter(
  (v) => v.value !== "liveEac",
);

const COL_DEFS = [
  { key: "contractId", label: "Contract ID" },
  { key: "contractName", label: "Contract Name" },
  { key: "live", label: "Live EAC" },
  { key: "comparison", label: "Comparison" },
  { key: "variance", label: "Variance" },
];

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* ══════════════════════════════════════════════════════════════ */
/*  Filter / operator helpers — see plTableFilterUtils.js         */
/* ══════════════════════════════════════════════════════════════ */

function buildApiFilters(filters) {
  return toReportApiFilters(filters, [
    "year",
    "contractId",
    "contractName",
    "customerName",
    "projectId",
    "orgId",
    "bu",
    "contractType",
    "clinLvl2",
    "businessType",
    "awardType",
    "competitionType",
    "popEndDate",
    "sector",
    "monthName",
    "quarter",
  ]);
}

/** Tab 2 `params.key`: array, omitted/"ALL" for every line item. */
function resolveByProjKeyParam(selectedKeys) {
  if (!Array.isArray(selectedKeys) || selectedKeys.length === 0) return "ALL";
  return selectedKeys;
}

/* ══════════════════════════════════════════════════════════════ */
/*  Number formatters                                              */
/* ══════════════════════════════════════════════════════════════ */
function fmtDollar(v) {
  if (v === 0) return "—";
  const abs = Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return v < 0 ? `($${abs})` : `$${abs}`;
}

function fmtVal(v, isPct) {
  if (isPct) return v === 0 ? "—" : `${v.toFixed(1)}%`;
  return fmtDollar(v);
}

function fmtVar(v, isPct) {
  const n = Number(v);
  const val = Number.isFinite(n) ? n : 0;
  if (isPct) {
    if (val === 0) return "0.0%";
    return val > 0 ? `+${val.toFixed(1)}%` : `(${Math.abs(val).toFixed(1)}%)`;
  }
  if (val === 0) return "$0";
  const abs = Math.abs(val).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  return val < 0 ? `($${abs})` : `$${abs}`;
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                         */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  tableSection: {
    marginBottom: 24,
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "visible",
  },
  tableSectionHead: {
    padding: "16px 20px 14px 24px",
    borderBottom: `1px solid ${HAIRLINE}`,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    position: "relative",
    zIndex: 20,
    overflow: "visible",
  },
  headLeft: { minWidth: 0, flex: 1 },
  tableSectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: TEXT_PRIMARY,
    letterSpacing: "-0.02em",
    lineHeight: 1.35,
    fontFamily: PL_FONT,
    margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 400,
    color: TEXT_MUTED,
    fontFamily: PL_FONT,
    lineHeight: 1.4,
  },
  pickerRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 12,
    flexWrap: "wrap",
  },

  /* column picker */
  colPickerWrap: { position: "relative", flexShrink: 0 },
  colsBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E2E8F0",
    background: "#fff",
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    fontFamily: PL_FONT,
    "&:hover": { background: "#F9FAFB", borderColor: "#C7D2FE" },
  },
  colsBtnActive: { borderColor: "#C7D2FE", background: ACCENT2, color: ACCENT },
  hiddenBadge: {
    display: "inline-flex",
    alignItems: "center",
    height: 18,
    padding: "0 7px",
    borderRadius: 10,
    background: ACCENT,
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
  },
  colPicker: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    zIndex: 1200,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
    minWidth: 180,
    maxHeight: 420,
    overflowY: "auto",
    fontFamily: PL_FONT,
  },
  colPickerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px 8px",
    borderBottom: "1px solid #F3F4F6",
  },
  colPickerTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  colPickerReset: {
    fontSize: 11,
    fontWeight: 600,
    color: ACCENT,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    fontFamily: PL_FONT,
    "&:hover": { textDecoration: "underline" },
  },
  colPickerItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "7px 14px",
    cursor: "pointer",
    fontSize: 12,
    color: "#374151",
    userSelect: "none",
    borderBottom: "1px solid #F9FAFB",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { background: "#F9FAFB" },
  },
  colPickerCheck: {
    width: 14,
    height: 14,
    accentColor: ACCENT,
    cursor: "pointer",
    flexShrink: 0,
  },

  /* table layout — fixed ID/Name pane + horizontally scrollable metrics.
     Fixed columns are not position:sticky-left (that jumps and lets scroll
     cells cover the existing header edge). */
  tableOuter: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" },
  tableSplit: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    maxHeight: "calc(100vh - 320px)",
    background: "#FFFFFF",
    alignItems: "stretch",
  },
  fixedPaneOuter: {
    flexShrink: 0,
    zIndex: 2,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    background: "#FFFFFF",
    width: "max-content",
    maxWidth: "50%",
  },
  fixedPane: {
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1,
    minHeight: 0,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  scrollPane: {
    overflow: "auto",
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    background: "#FFFFFF",
  },
  table: {
    width: "max-content",
    minWidth: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontFamily: PL_FONT,
    fontSize: 13,
    tableLayout: "auto",
  },
  tableFixed: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontFamily: PL_FONT,
    fontSize: 13,
    tableLayout: "auto",
  },

  theadSticky: {
    background: HEADER_ROW,
  },
  /* Group header row (Live AC / Comparison / Variance) sticks at the very top;
     applied as an explicit class per-cell rather than a nested tr:nth-child
     selector, since the latter wasn't reliably taking effect. */
  thStickyGroup: {
    position: "sticky",
    top: 0,
    zIndex: 4,
    height: GROUP_ROW_H,
    boxSizing: "border-box",
    background: HEADER_ROW,
    boxShadow: `0 1px 0 ${PL_TH_BORDER}`,
  },
  /* Measure header row sticks right below the group row, not on top of it. */
  thStickyMeasure: {
    position: "sticky",
    top: `var(--group-row-h, ${GROUP_ROW_H}px)`,
    zIndex: 3,
    background: HEADER_ROW,
    boxShadow: `0 1px 0 ${PL_TH_BORDER}`,
  },

  /* header bands */
  thProjects: {
    background: HEADER_TOP,
    color: HEADER_LABEL,
    padding: "14px 20px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "left",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    verticalAlign: "middle",
    borderBottom: `1px solid ${PL_TH_BORDER}`,
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandLive: {
    background: HEADER_TOP,
    color: HEADER_LABEL,
    padding: "12px 14px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "center",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${PL_TH_BORDER}`,
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandComp: {
    background: HEADER_TOP,
    color: HEADER_LABEL,
    padding: "12px 14px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "center",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${PL_TH_BORDER}`,
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandVar: {
    background: HEADER_TOP,
    color: HEADER_LABEL,
    padding: "12px 14px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "center",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${PL_TH_BORDER}`,
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },

  /* column headers */
  thColId: {
    background: HEADER_ROW,
    color: HEADER_LABEL,
    padding: "12px 20px",
    fontWeight: 500,
    fontSize: 12,
    textAlign: "left",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.3,
    borderRight: `1px solid ${PL_TH_BORDER}`,
    cursor: "pointer",
    userSelect: "none",
  },
  thColName: {
    background: HEADER_ROW,
    color: HEADER_LABEL,
    padding: "12px 14px",
    fontWeight: 500,
    fontSize: 12,
    textAlign: "left",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.3,
    width: "var(--name-col-max, 280px)",
    maxWidth: "var(--name-col-max, 280px)",
    borderRight: `1px solid ${PL_TH_BORDER}`,
    cursor: "pointer",
    userSelect: "none",
  },
  thColLive: {
    background: HEADER_ROW,
    color: HEADER_LABEL,
    padding: "12px 14px",
    fontWeight: 600,
    fontSize: 12,
    textAlign: "right",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.3,
    borderRight: `1px solid ${PL_TH_BORDER}`,
    cursor: "pointer",
    userSelect: "none",
  },
  thColComp: {
    background: HEADER_ROW,
    color: HEADER_LABEL,
    padding: "12px 14px",
    fontWeight: 600,
    fontSize: 12,
    textAlign: "right",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.3,
    borderRight: `1px solid ${PL_TH_BORDER}`,
    cursor: "pointer",
    userSelect: "none",
  },
  thColVar: {
    background: HEADER_ROW,
    color: HEADER_LABEL,
    padding: "12px 14px",
    fontWeight: 600,
    fontSize: 12,
    textAlign: "right",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.3,
    borderRight: `1px solid ${PL_TH_BORDER}`,
    cursor: "pointer",
    userSelect: "none",
  },

  /* floating filter row */
  trFilter: { background: "#F1F5F9", borderBottom: `2px solid ${HAIRLINE}` },
  filterTextCell: { padding: "5px 10px", background: "#F1F5F9" },
  filterNumCell: { padding: "5px 6px", background: "#F1F5F9" },
  filterWrap: { display: "flex", alignItems: "center", gap: 3, height: 26 },
  filterTextInput: {
    flex: 1,
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 7px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    "&:focus": { borderColor: ACCENT },
    "&::placeholder": { color: "#9CA3AF" },
  },
  filterNumInput: {
    width: "100%",
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 5px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    textAlign: "right",
    "&:focus": { borderColor: ACCENT },
    "&::placeholder": { color: "#CBD5E1" },
  },
  filterOpBtn: {
    flexShrink: 0,
    width: 22,
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    background: "#fff",
    fontSize: 11,
    fontWeight: 700,
    color: ACCENT,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontFamily: PL_FONT,
    "&:hover": { background: ACCENT2, borderColor: ACCENT },
  },
  filterOpBtnActive: {
    background: ACCENT,
    borderColor: ACCENT,
    color: "#fff",
    "&:hover": { background: "#4338CA" },
  },
  filterClearBtn: {
    flexShrink: 0,
    width: 18,
    height: 18,
    border: "none",
    borderRadius: "50%",
    background: "#E2E8F0",
    fontSize: 9,
    color: "#64748B",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": { background: "#CBD5E1" },
  },

  /* operator dropdown (portal) */
  opDropdown: {
    position: "fixed",
    zIndex: 100,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    minWidth: 190,
    overflow: "hidden",
    fontFamily: PL_FONT,
  },
  opDropdownItem: {
    display: "block",
    width: "100%",
    padding: "8px 14px",
    fontSize: 12,
    color: "#374151",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: PL_FONT,
    "&:hover": { background: ACCENT2, color: ACCENT },
  },
  opDropdownItemActive: { background: ACCENT2, color: ACCENT, fontWeight: 600 },

  /* data rows */
  trStripe: { "& td": { background: ROW_STRIPE } },
  tdId: {
    padding: "13px 20px",
    color: TEXT_MUTED,
    fontSize: 12,
    fontWeight: 600,
    borderBottom: `1px solid ${HAIRLINE}`,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.35,
    verticalAlign: "middle",
    background: "#fff",
  },
  tdName: {
    padding: "13px 14px",
    color: TEXT_PRIMARY,
    fontSize: 13,
    fontWeight: 400,
    borderBottom: `1px solid ${HAIRLINE}`,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.35,
    verticalAlign: "middle",
    width: "var(--name-col-max, 280px)",
    maxWidth: "var(--name-col-max, 280px)",
    background: "#fff",
  },
  tdLive: {
    padding: "13px 14px",
    color: "#374151",
    fontSize: 13,
    textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`,
    whiteSpace: "nowrap",
    overflow: "hidden",
    verticalAlign: "middle",
    fontVariantNumeric: "tabular-nums",
    background: "#fff",
  },
  tdComp: {
    padding: "13px 14px",
    color: FORECAST_C,
    fontSize: 13,
    textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`,
    whiteSpace: "nowrap",
    overflow: "hidden",
    verticalAlign: "middle",
    fontVariantNumeric: "tabular-nums",
    background: "#fff",
  },
  tdVar: {
    padding: "13px 14px",
    fontSize: 13,
    fontWeight: 600,
    textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`,
    whiteSpace: "nowrap",
    overflow: "hidden",
    verticalAlign: "middle",
    fontVariantNumeric: "tabular-nums",
    background: "#fff",
  },

  /* total row — sticky below header */
  trTotalSticky: {
    "& td": {
      position: "sticky",
      top: "var(--sticky-total-top, 52px)",
      zIndex: 2,
      background: TOTAL_BG,
      fontWeight: 700,
      boxShadow: `0 1px 0 ${HAIRLINE}`,
    },
  },
  tdTotalId: {
    padding: "14px 20px",
    color: TEXT_MUTED,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.35,
  },
  tdTotalLabel: {
    padding: "14px 14px",
    color: TEXT_PRIMARY,
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: 1.35,
    verticalAlign: "middle",
    width: "var(--name-col-max, 280px)",
    maxWidth: "var(--name-col-max, 280px)",
  },
  tdTotalNum: {
    padding: "14px 14px",
    fontSize: 13,
    fontWeight: 700,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  tdTotalVar: {
    padding: "14px 14px",
    fontSize: 13,
    fontWeight: 700,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },

  /* pagination */
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 20px",
    borderTop: `1px solid ${HAIRLINE}`,
    background: "#FAFAFA",
    flexShrink: 0,
    gap: 12,
    flexWrap: "wrap",
  },
  paginationInfo: {
    fontSize: 12,
    color: TEXT_MUTED,
    fontFamily: PL_FONT,
    whiteSpace: "nowrap",
  },
  paginationControls: { display: "flex", alignItems: "center", gap: 3 },
  pageBtn: {
    minWidth: 30,
    height: 28,
    padding: "0 6px",
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    background: "#fff",
    fontSize: 12,
    color: TH_BG_BAND,
    cursor: "pointer",
    fontFamily: PL_FONT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    "&:hover:not(:disabled)": {
      background: "#F1F5F9",
      borderColor: "#94A3B8",
      color: TH_BG,
    },
    "&:disabled": { opacity: 0.35, cursor: "default" },
  },
  pageBtnActive: {
    background: TH_BG_BAND,
    borderColor: TH_BG_BAND,
    color: "#fff",
    fontWeight: 600,
    "&:hover": { background: TH_BG_BAND },
  },
  pageDots: {
    fontSize: 12,
    color: TEXT_MUTED,
    padding: "0 4px",
    userSelect: "none",
  },
  paginationRight: { display: "flex", alignItems: "center", gap: 8 },
  pageSizeLabel: {
    fontSize: 12,
    color: TEXT_MUTED,
    fontFamily: PL_FONT,
    whiteSpace: "nowrap",
  },
  pageSizeSelect: {
    height: 28,
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    fontSize: 12,
    color: "#374151",
    padding: "0 6px",
    background: "#fff",
    cursor: "pointer",
    outline: "none",
    fontFamily: PL_FONT,
    "&:focus": { borderColor: ACCENT },
  },
  filterIconBtn: {
    flexShrink: 0,
    width: 16,
    height: 16,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    borderRadius: 3,
    color: PL_TH_TEXT,
    opacity: 0.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { opacity: 1, background: "rgba(255,255,255,0.14)" },
  },
  filterIconBtnActive: {
    opacity: 1,
    color: "#A5B4FC",
    background: "rgba(165,180,252,0.18)",
    borderRadius: 3,
  },
  filterPanel: {
    position: "fixed",
    zIndex: 99,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: "10px 12px",
    minWidth: 180,
    fontFamily: PL_FONT,
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  FilterGroup — matches PlFilterGroup pill design                */
/* ══════════════════════════════════════════════════════════════ */
function FilterFunnelIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function FilterGroup({
  label,
  options,
  value,
  onChange,
  classes,
  minWidth = 120,
}) {
  return (
    <div className={classes.filterGroup}>
      <span className={classes.filterGroupLabel}>{label}</span>
      <select
        className={classes.filterGroupSelect}
        style={{ minWidth }}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main component                                                 */
/* ══════════════════════════════════════════════════════════════ */
export default function CurrentMonthActualsVsForecastByProj({
  filters,
  actualsThroughDate,
  onParamsChange,
}) {
  const plClasses = usePlByProgramStyles();
  const localClasses = useStyles();
  useReportFonts();
  const classes = { ...plClasses, ...localClasses };

  /* ── API state ── */
  const [apiData, setApiData] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  /* Multi-select of metric keys (PlMultiSelect [] = All). contracts[]/totals always
     include all 11 line items; params.key selects download columns and is echoed as
     data.keys. */
  const [selectedKeys, setSelectedKeys] = useState(["contractRevenue"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [versionOptions, setVersionOptions] = useState(VERSION_FALLBACK);

  const keyParam = useMemo(() => resolveByProjKeyParam(selectedKeys), [selectedKeys]);

  useEffect(() => {
    onParamsChange?.({
      ...(selectedVersion ? { version: selectedVersion } : {}),
      key: keyParam,
    });
  }, [selectedVersion, keyParam, onParamsChange]);

  /* ── Column visibility ── */
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);

  /* ── Pagination ── */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ── Sorting — array of {key, dir} for multi-level sort ── */
  const [sortKeys, setSortKeys] = useState([]);

  /* ── Row filters ── */
  const [searchId, setSearchId] = useState("");
  const [searchName, setSearchName] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [openOpKey, setOpenOpKey] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const opBtnRefs = useRef({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  /* ── Table scroll / sticky header height ── */
  const fixedWrapRef = useRef(null);
  const scrollWrapRef = useRef(null);
  const scrollTheadRef = useRef(null);
  const groupRowRef = useRef(null);
  const syncingScroll = useRef(false);
  /* Header is now two rows (group row + measure row) since the Key multi-select
     can render several metric columns — taller than the old single-row default. */
  const [headerHeight, setHeaderHeight] = useState(88);
  /* The group row's height is only a CSS *minimum* in table layout — if content
     needs more than GROUP_ROW_H, the row silently grows taller and the measure
     row's sticky "top" falls out of sync. Measuring the real rendered height
     keeps them locked together. */
  const [groupRowHeight, setGroupRowHeight] = useState(GROUP_ROW_H);

  const measureHeader = useCallback(() => {
    if (scrollTheadRef.current) {
      const h = scrollTheadRef.current.offsetHeight;
      setHeaderHeight((prev) => (prev === h ? prev : h));
    }
    if (groupRowRef.current) {
      const h = groupRowRef.current.offsetHeight;
      setGroupRowHeight((prev) => (prev === h ? prev : h));
    }
  }, []);

  const syncRowHeights = useCallback(() => {
    const leftTable = fixedWrapRef.current?.querySelector("table");
    const rightTable = scrollWrapRef.current?.querySelector("table");
    if (leftTable && rightTable) {
      const apply = (aRows, bRows) => {
        const n = Math.min(aRows.length, bRows.length);
        for (let i = 0; i < n; i++) {
          aRows[i].style.height = "";
          bRows[i].style.height = "";
        }
        for (let i = 0; i < n; i++) {
          const h = Math.ceil(Math.max(aRows[i].offsetHeight, bRows[i].offsetHeight));
          aRows[i].style.height = `${h}px`;
          bRows[i].style.height = `${h}px`;
        }
      };
      apply(
        Array.from(leftTable.querySelectorAll("thead tr")),
        Array.from(rightTable.querySelectorAll("thead tr")),
      );
      apply(
        Array.from(leftTable.querySelectorAll("tbody tr")),
        Array.from(rightTable.querySelectorAll("tbody tr")),
      );
    }
    measureHeader();
  }, [measureHeader]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) {
      const btn = filterBtnRefs.current[key];
      if (btn)
        setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    }
    const opKey = openOpKeyRef.current;
    if (opKey) {
      const opBtn = opBtnRefs.current[opKey];
      if (opBtn) setDropdownPos(calcOpDropdownPos(opBtn));
    }
  }, []);

  const onFixedPaneScroll = useCallback(() => {
    if (syncingScroll.current) return;
    syncingScroll.current = true;
    if (scrollWrapRef.current && fixedWrapRef.current) {
      scrollWrapRef.current.scrollTop = fixedWrapRef.current.scrollTop;
    }
    syncingScroll.current = false;
    repositionPanels();
  }, [repositionPanels]);

  const onScrollPaneScroll = useCallback(() => {
    if (syncingScroll.current) return;
    syncingScroll.current = true;
    if (scrollWrapRef.current && fixedWrapRef.current) {
      fixedWrapRef.current.scrollTop = scrollWrapRef.current.scrollTop;
    }
    syncingScroll.current = false;
    repositionPanels();
  }, [repositionPanels]);

  /* Keep filter popovers anchored during page/table scroll */
  useEffect(() => {
    const panes = [fixedWrapRef.current, scrollWrapRef.current].filter(Boolean);
    const onScroll = () => repositionPanels();
    panes.forEach((el) => el.addEventListener("scroll", onScroll, { passive: true }));
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => {
      panes.forEach((el) => el.removeEventListener("scroll", onScroll));
      window.removeEventListener("scroll", repositionPanels, true);
      window.removeEventListener("resize", repositionPanels);
    };
  }, [repositionPanels]);

  const apiFilterKey = useMemo(
    () => getApiFilterKey(filters, buildApiFilters),
    [filters],
  );

  /* ── Fetch on filters or version change — Key is client-side only ── */
  useEffect(() => {
    if (!selectedVersion) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCmActualsVsForecastByProjAPI(
      buildApiFilters(filters),
      selectedVersion,
      "ALL",
    )
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 200 && res.data) {
          setApiData(res.data);
        } else {
          setApiData(null);
        }
      })
      .catch(() => {
        if (!cancelled) setApiData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiFilterKey, selectedVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Reset to page 1 when filters or search change — not on sort */
  useEffect(() => {
    setPage(1);
  }, [filters, selectedVersion, searchId, searchName, colFilters]);

  /* Fetch available versions for the selected year — exclude Live EAC */
  const currentYear =
    filters?.year && filters.year !== "All"
      ? filters.year
      : String(new Date().getFullYear());
  useEffect(() => {
    let cancelled = false;
    setSelectedVersion(null);
    setLoading(true);

    const applyVersionOptions = (opts) => {
      if (cancelled || !opts.length) return;
      setVersionOptions(opts);
      setSelectedVersion((prev) =>
        prev && opts.some((o) => o.value === prev) ? prev : opts[0].value,
      );
    };

    getVersionsAPI(currentYear)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.length) {
          const opts = res.data
            .filter(
              (v) =>
                v.key !== "liveEac" && v.name?.toLowerCase() !== "live eac",
            )
            .map((v) => ({ value: v.key, label: v.name }));
          if (opts.length) {
            applyVersionOptions(opts);
            return;
          }
        }
        applyVersionOptions(VERSION_FALLBACK);
      })
      .catch(() => {
        if (!cancelled) applyVersionOptions(VERSION_FALLBACK);
      });

    return () => {
      cancelled = true;
    };
  }, [currentYear]);

  /* ── Derived values ── */
  const rowDefs = apiData?.rows ?? [];
  const contracts = apiData?.contracts ?? [];
  const totals = apiData?.totals ?? { live: {}, comparison: {} };
  const liveLabel = apiData?.liveVersion?.name ?? "Live EAC";
  const compLabel = apiData?.comparisonVersion?.name ?? "Comparison";
  const year =
    filters?.year && filters.year !== "All"
      ? filters.year
      : String(new Date().getFullYear());
  const liveYear = String(new Date().getFullYear());

  /* [] = All (matches PlMultiSelect "All" convention). Every selected metric is
     treated uniformly — matches EAC Variance View's model: Live AC / Comparison /
     Variance are the three fixed groups, each measure gets one sub-column per
     group, and every sub-column is independently sortable/filterable. There is
     no distinguished "primary" metric anymore. */
  const effectiveKeys =
    selectedKeys.length === 0 ? rowDefs.map((r) => r.key) : selectedKeys;
  const getValForKey = (obj, key) => {
    const def = rowDefs.find((r) => r.key === key);
    const v = (obj ?? {})[key] ?? 0;
    return def?.isPct ? v * 100 : v;
  };
  const sumContractsFor = (key, getter) => {
    if (!filteredContracts.length) return 0;
    const def = rowDefs.find((r) => r.key === key);
    const sum = filteredContracts.reduce((s, c) => s + getter(c), 0);
    return def?.isPct ? sum / filteredContracts.length : sum;
  };

  const liveCutoffMonth = apiData?.actualThroughMonth ?? apiData?.liveCutoffMonth ?? null;
  const liveActualsThrough = liveCutoffMonth
    ? MONTH_NAMES[liveCutoffMonth - 1]
    : null;
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(liveCutoffMonth),
    formatActualsThroughFromLabel(liveActualsThrough),
  );

  useLayoutEffect(() => {
    syncRowHeights();
    const ro = new ResizeObserver(() => syncRowHeights());
    if (fixedWrapRef.current) ro.observe(fixedWrapRef.current);
    if (scrollWrapRef.current) ro.observe(scrollWrapRef.current);
    window.addEventListener("resize", syncRowHeights);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncRowHeights);
    };
  }, [
    syncRowHeights,
    hiddenCols,
    effectiveKeys.join("|"),
    liveLabel,
    compLabel,
    loading,
    page,
    pageSize,
    searchId,
    searchName,
    colFilters,
    sortKeys,
    apiData,
  ]);

  const varColor = (v) => (v === 0 ? "#94A3B8" : v > 0 ? PL_POS_C : PL_NEG_C);

  /* ── Operator dropdown ── */
  const updateColFilter = (key, field, value) =>
    setColFilters((prev) => ({
      ...prev,
      [key]: { op: "gt", ...prev[key], [field]: value },
    }));

  const clearColFilter = (key) =>
    setColFilters((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });

  const isNumActive = (key) => isNumFilterActive(colFilters[key]);

  const toggleOpDropdown = (key) => {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  };

  const selectOp = (key, op) => {
    updateColFilter(key, "op", op);
    setOpenOpKey(null);
  };

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-dropdown]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-dropdown]")
      )
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!colPickerOpen) return;
    const h = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target))
        setColPickerOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [colPickerOpen]);

  /* ── Column visibility ── */
  const toggleCol = (key) =>
    setHiddenCols((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  const showAllCols = () => setHiddenCols(new Set());
  const hiddenCount = hiddenCols.size;
  const showContractId = !hiddenCols.has("contractId");
  const showContractName = !hiddenCols.has("contractName");
  const hasFixedPane = showContractId || showContractName;
  const metricColCount =
    (hiddenCols.has("live") ? 0 : effectiveKeys.length) +
    (hiddenCols.has("comparison") ? 0 : effectiveKeys.length) +
    (hiddenCols.has("variance") ? 0 : effectiveKeys.length);
  const nameColMaxW = nameColMaxWidth(metricColCount);

  /* Column keys for the metric groups are `${group}_${metricKey}` (group ∈
     live/comparison/variance) — matches EAC Variance View's addressing scheme. */
  const getGroupValue = (row, group, key) => {
    if (group === "live") return getValForKey(row.live, key);
    if (group === "comparison") return getValForKey(row.comparison, key);
    return getValForKey(row.live, key) - getValForKey(row.comparison, key); // variance
  };
  const splitColKey = (colKey) => {
    const us = colKey.indexOf("_");
    if (us <= 0) return null;
    const group = colKey.slice(0, us);
    const key = colKey.slice(us + 1);
    if (!["live", "comparison", "variance"].includes(group)) return null;
    return { group, key };
  };

  /* ── Row filter ── */
  const rowPasses = (c) => {
    if (
      searchId &&
      !c.contractId.toLowerCase().includes(searchId.toLowerCase())
    )
      return false;
    if (
      searchName &&
      !c.contractName.toLowerCase().includes(searchName.toLowerCase())
    )
      return false;
    for (const key of effectiveKeys) {
      if (!applyNumFilter(getGroupValue(c, "live", key), colFilters[`live_${key}`])) return false;
      if (!applyNumFilter(getGroupValue(c, "comparison", key), colFilters[`comparison_${key}`])) return false;
      if (!applyNumFilter(getGroupValue(c, "variance", key), colFilters[`variance_${key}`])) return false;
    }
    return true;
  };

  if (loading) return <Loading />;

  /* ── Multi-level sort ── */
  const handleSort = (colKey) => {
    setSortKeys((prev) => {
      const existing = prev.find((s) => s.key === colKey);
      if (!existing) return [...prev, { key: colKey, dir: "asc" }];
      if (existing.dir === "asc")
        return prev.map((s) => (s.key === colKey ? { ...s, dir: "desc" } : s));
      return prev.filter((s) => s.key !== colKey);
    });
  };

  const sortComparator = (a, b) => {
    for (const { key, dir } of sortKeys) {
      let av, bv;
      if (key === "contractId") {
        av = a.contractId;
        bv = b.contractId;
      } else if (key === "contractName") {
        av = a.contractName;
        bv = b.contractName;
      } else {
        const split = splitColKey(key);
        if (split) {
          av = getGroupValue(a, split.group, split.key);
          bv = getGroupValue(b, split.group, split.key);
        }
      }
      const cmp =
        typeof av === "string"
          ? av.localeCompare(bv ?? "")
          : (av ?? 0) - (bv ?? 0);
      if (cmp !== 0) return dir === "asc" ? cmp : -cmp;
    }
    return 0;
  };

  const getSortColumnLabel = (colKey) => {
    if (colKey === "contractId") return "Contract ID";
    if (colKey === "contractName") return "Contract Name";
    const split = splitColKey(colKey);
    if (split) {
      const metricLabel = rowDefs.find((r) => r.key === split.key)?.label ?? split.key;
      if (split.group === "live") return `${liveLabel} ${metricLabel}`.trim();
      if (split.group === "comparison") return `${compLabel} ${metricLabel}`.trim();
      return `Variance ${metricLabel}`.trim();
    }
    return COL_DEFS.find((c) => c.key === colKey)?.label ?? colKey;
  };

  const sortIcon = (colKey) => {
    const sortTitle = `Sort by ${getSortColumnLabel(colKey)}`;
    const idx = sortKeys.findIndex((s) => s.key === colKey);
    if (idx === -1) {
      return (
        <span
          title={sortTitle}
          style={{
            fontSize: 8,
            color: "rgba(255,255,255,0.4)",
            marginLeft: 3,
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            lineHeight: 1,
            flexShrink: 0,
            gap: 0,
          }}
        >
          <span>▲</span>
          <span>▼</span>
        </span>
      );
    }
    const { dir } = sortKeys[idx];
    return (
      <span
        title={sortTitle}
        style={{
          fontSize: 9,
          color: "#FFFFFF",
          marginLeft: 3,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        {dir === "asc" ? "▲" : "▼"}
        {sortKeys.length > 1 && (
          <sup style={{ fontSize: 8, lineHeight: 1 }}>{idx + 1}</sup>
        )}
      </span>
    );
  };

  const filteredContracts = contracts.filter(rowPasses);
  const visibleContracts =
    sortKeys.length === 0
      ? filteredContracts
      : [...filteredContracts].sort(sortComparator);

  const totalPages = Math.max(1, Math.ceil(visibleContracts.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedContracts = visibleContracts.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );
  const rangeStart =
    visibleContracts.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, visibleContracts.length);

  const hasTableFilters =
    searchId ||
    searchName ||
    effectiveKeys.some(
      (key) =>
        isNumActive(`live_${key}`) ||
        isNumActive(`comparison_${key}`) ||
        isNumActive(`variance_${key}`),
    );

  /* One entry per selected metric — each carries its own Live/Comparison/Variance
     totals, rendered as three sub-columns under the fixed Live AC / Comparison /
     Variance group headers (see thead below). */
  const columns = effectiveKeys.map((key) => {
    const def = rowDefs.find((r) => r.key === key);
    const live = hasTableFilters
      ? sumContractsFor(key, (c) => getValForKey(c.live, key))
      : getValForKey(totals.live, key);
    const comp = hasTableFilters
      ? sumContractsFor(key, (c) => getValForKey(c.comparison, key))
      : getValForKey(totals.comparison, key);
    return {
      key,
      label: def?.label ?? key,
      isPct: def?.isPct ?? false,
      totalLive: live,
      totalComp: comp,
      totalVar: live - comp,
    };
  });

  /* page number array with ellipsis logic */
  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();

  /* filter icon helpers */
  const renderNumFilterDiv = (colKey) =>
    renderStandardNumFilter({
      label: getSortColumnLabel(colKey),
      colKey,
      colFilters,
      updateColFilter,
      clearColFilter,
      toggleOpDropdown,
      opBtnRefs,
    });

  const hasFilterValue = (key) => {
    if (key === "contractId") return !!searchId;
    if (key === "contractName") return !!searchName;
    return isNumActive(key);
  };

  const renderFilterContent = (key) => {
    if (key === "contractId")
      return renderStandardTextFilter({
        label: "Contract ID",
        value: searchId,
        onChange: setSearchId,
        onClear: () => setSearchId(""),
        placeholder: "Search ID…",
      });
    if (key === "contractName")
      return renderStandardTextFilter({
        label: "Contract Name",
        value: searchName,
        onChange: setSearchName,
        onClear: () => setSearchName(""),
        placeholder: "Search name…",
      });
    return renderNumFilterDiv(key);
  };

  const toggleFilter = (key) => {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  /* helper: sort-aware th onClick — ignores clicks on the filter button */
  const onThClick = (colKey) => (e) => {
    if (
      e.target.closest("[data-filter-btn]") ||
      e.target.closest("[data-op-btn]")
    )
      return;
    handleSort(colKey);
  };

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <PlPageHeader
          classes={classes}
          title="Current Month Actuals Vs Forecast"
          accent="By Project"
          meta="Project-level Live EAC Vs. Forecast Comparison"
          metaTop={
            liveActualsThrough
              ? `Actuals through ${liveActualsThrough} – ${liveYear}`
              : `FY-${year}`
          }
          date={headerDate}
        />

        <div className={classes.tableSection}>
          {/* ── Section head ── */}
          <div className={classes.tableSectionHead}>
            <div className={classes.headLeft}>
              <h2 className={classes.tableSectionTitle}>Project Breakdown</h2>
              <div className={classes.tableSectionMeta}>
                FY-{year} · {visibleContracts.length} of {contracts.length}{" "}
                Contracts ·{" "}
                {columns.length === 1 ? columns[0].label : `${columns.length} Metrics`}
                {sortKeys.length > 0 && (
                  <span
                    style={{ marginLeft: 8, color: ACCENT, fontWeight: 500 }}
                  >
                    · Sorted By{" "}
                    {sortKeys
                      .map((s) =>
                        s.key === "contractId"
                          ? "ID"
                          : s.key === "contractName"
                            ? "Name"
                            : getSortColumnLabel(s.key),
                      )
                      .join(", ")}
                  </span>
                )}
              </div>
              <div className={classes.pickerRow}>
                <PlMultiSelect
                  label="Key"
                  options={rowDefs.map((r) => r.label)}
                  value={selectedKeys
                    .map((k) => rowDefs.find((r) => r.key === k)?.label)
                    .filter(Boolean)}
                  onChange={(labels) => {
                    const keys = labels
                      .map((l) => rowDefs.find((r) => r.label === l)?.key)
                      .filter(Boolean);
                    setSelectedKeys(keys);
                  }}
                  classes={classes}
                />
                <FilterGroup
                  label="Version"
                  options={versionOptions}
                  value={selectedVersion}
                  onChange={setSelectedVersion}
                  classes={classes}
                  minWidth={140}
                />
              </div>
            </div>

            {/* ── Columns button ── */}
            <div className={classes.colPickerWrap} ref={colPickerRef}>
              <button
                className={`${classes.colsBtn} ${colPickerOpen ? classes.colsBtnActive : ""}`}
                onClick={() => setColPickerOpen((p) => !p)}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="18" />
                  <rect x="14" y="3" width="7" height="18" />
                </svg>
                Columns
                {hiddenCount > 0 && (
                  <span className={classes.hiddenBadge}>
                    {hiddenCount} Hidden
                  </span>
                )}
              </button>
              {colPickerOpen && (
                <div className={classes.colPicker}>
                  <div className={classes.colPickerHeader}>
                    <span className={classes.colPickerTitle}>Show / Hide</span>
                    {hiddenCount > 0 && (
                      <button
                        className={classes.colPickerReset}
                        onClick={showAllCols}
                      >
                        Show All
                      </button>
                    )}
                  </div>
                  {COL_DEFS.map((col) => (
                    <label key={col.key} className={classes.colPickerItem}>
                      <input
                        type="checkbox"
                        className={classes.colPickerCheck}
                        checked={!hiddenCols.has(col.key)}
                        onChange={() => toggleCol(col.key)}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Table ── */}
          <div className={classes.tableOuter}>
            <div
              className={classes.tableSplit}
              style={{
                "--sticky-total-top": `${headerHeight}px`,
                "--group-row-h": `${groupRowHeight}px`,
                "--name-col-max": `${nameColMaxW}px`,
              }}
            >
              {hasFixedPane && (
                <div className={classes.fixedPaneOuter}>
                  <div
                    ref={fixedWrapRef}
                    className={classes.fixedPane}
                    onScroll={onFixedPaneScroll}
                  >
                    <table className={classes.tableFixed}>
                      <thead className={classes.theadSticky}>
                        <tr>
                          {showContractId && (
                            <th className={`${classes.thColId} ${classes.thStickyGroup}`} aria-hidden="true" />
                          )}
                          {showContractName && (
                            <th className={`${classes.thColName} ${classes.thStickyGroup}`} aria-hidden="true" />
                          )}
                        </tr>
                        <tr>
                          {showContractId && (
                            <th
                              className={`${classes.thColId} ${classes.thStickyMeasure}`}
                              onClick={onThClick("contractId")}
                            >
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                                <span style={{ flex: 1, color: openFilterKey === "contractId" ? "#60A5FA" : undefined }}>
                                  Contract ID
                                </span>
                                {sortIcon("contractId")}
                                <button
                                  ref={(el) => { filterBtnRefs.current["contractId"] = el; }}
                                  data-filter-btn
                                  className={`${classes.filterIconBtn} ${hasFilterValue("contractId") ? classes.filterIconBtnActive : ""}`}
                                  onClick={(e) => { e.stopPropagation(); toggleFilter("contractId"); }}
                                  title="Filter"
                                >
                                  <FilterFunnelIcon />
                                </button>
                              </div>
                            </th>
                          )}
                          {showContractName && (
                            <th
                              className={`${classes.thColName} ${classes.thStickyMeasure}`}
                              onClick={onThClick("contractName")}
                            >
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                                <span style={{ flex: 1, color: openFilterKey === "contractName" ? "#60A5FA" : undefined }}>
                                  Contract Name
                                </span>
                                {sortIcon("contractName")}
                                <button
                                  ref={(el) => { filterBtnRefs.current["contractName"] = el; }}
                                  data-filter-btn
                                  className={`${classes.filterIconBtn} ${hasFilterValue("contractName") ? classes.filterIconBtnActive : ""}`}
                                  onClick={(e) => { e.stopPropagation(); toggleFilter("contractName"); }}
                                  title="Filter"
                                >
                                  <FilterFunnelIcon />
                                </button>
                              </div>
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className={classes.trTotalSticky}>
                          {showContractId && <td className={classes.tdTotalId}>Total</td>}
                          {showContractName && <td className={classes.tdTotalLabel}>All Contracts</td>}
                        </tr>
                        {pagedContracts.map((row, idx) => {
                          const globalIdx = (safePage - 1) * pageSize + idx;
                          return (
                            <tr key={row.contractId} className={globalIdx % 2 === 1 ? classes.trStripe : ""}>
                              {showContractId && <td className={classes.tdId}>{row.contractId}</td>}
                              {showContractName && (
                                <td className={classes.tdName} title={row.contractName}>
                                  {row.contractName}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div
                ref={scrollWrapRef}
                className={classes.scrollPane}
                onScroll={onScrollPaneScroll}
              >
                <table className={classes.table}>
                  <thead ref={scrollTheadRef} className={classes.theadSticky}>
                    <tr ref={groupRowRef}>
                      {!hiddenCols.has("live") && effectiveKeys.length > 0 && (
                        <th colSpan={effectiveKeys.length} className={`${classes.thColLive} ${classes.thStickyGroup}`} style={{ textAlign: "center" }}>
                          {liveLabel}
                        </th>
                      )}
                      {!hiddenCols.has("comparison") && effectiveKeys.length > 0 && (
                        <th colSpan={effectiveKeys.length} className={`${classes.thColComp} ${classes.thStickyGroup}`} style={{ textAlign: "center" }}>
                          {compLabel}
                        </th>
                      )}
                      {!hiddenCols.has("variance") && effectiveKeys.length > 0 && (
                        <th colSpan={effectiveKeys.length} className={`${classes.thColVar} ${classes.thStickyGroup}`} style={{ textAlign: "center" }}>
                          Variance
                        </th>
                      )}
                    </tr>
                    <tr>
                      {!hiddenCols.has("live") &&
                        effectiveKeys.map((key) => {
                          const colKey = `live_${key}`;
                          const label = rowDefs.find((r) => r.key === key)?.label ?? key;
                          const fActive = openFilterKey === colKey || hasFilterValue(colKey);
                          return (
                            <th key={colKey} className={`${classes.thColLive} ${classes.thStickyMeasure}`} onClick={onThClick(colKey)}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                                <span style={{ color: openFilterKey === colKey ? "#60A5FA" : undefined }}>
                                  {label}
                                </span>
                                {sortIcon(colKey)}
                                <button
                                  ref={(el) => { filterBtnRefs.current[colKey] = el; }}
                                  data-filter-btn
                                  className={`${classes.filterIconBtn} ${fActive ? classes.filterIconBtnActive : ""}`}
                                  onClick={(e) => { e.stopPropagation(); toggleFilter(colKey); }}
                                  title="Filter"
                                >
                                  <FilterFunnelIcon />
                                </button>
                              </div>
                            </th>
                          );
                        })}
                      {!hiddenCols.has("comparison") &&
                        effectiveKeys.map((key) => {
                          const colKey = `comparison_${key}`;
                          const label = rowDefs.find((r) => r.key === key)?.label ?? key;
                          const fActive = openFilterKey === colKey || hasFilterValue(colKey);
                          return (
                            <th key={colKey} className={`${classes.thColComp} ${classes.thStickyMeasure}`} onClick={onThClick(colKey)}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                                <span style={{ color: openFilterKey === colKey ? "#60A5FA" : undefined }}>
                                  {label}
                                </span>
                                {sortIcon(colKey)}
                                <button
                                  ref={(el) => { filterBtnRefs.current[colKey] = el; }}
                                  data-filter-btn
                                  className={`${classes.filterIconBtn} ${fActive ? classes.filterIconBtnActive : ""}`}
                                  onClick={(e) => { e.stopPropagation(); toggleFilter(colKey); }}
                                  title="Filter"
                                >
                                  <FilterFunnelIcon />
                                </button>
                              </div>
                            </th>
                          );
                        })}
                      {!hiddenCols.has("variance") &&
                        effectiveKeys.map((key) => {
                          const colKey = `variance_${key}`;
                          const label = rowDefs.find((r) => r.key === key)?.label ?? key;
                          const fActive = openFilterKey === colKey || hasFilterValue(colKey);
                          return (
                            <th key={colKey} className={`${classes.thColVar} ${classes.thStickyMeasure}`} onClick={onThClick(colKey)}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                                <span style={{ color: openFilterKey === colKey ? "#60A5FA" : undefined }}>
                                  {label}
                                </span>
                                {sortIcon(colKey)}
                                <button
                                  ref={(el) => { filterBtnRefs.current[colKey] = el; }}
                                  data-filter-btn
                                  className={`${classes.filterIconBtn} ${fActive ? classes.filterIconBtnActive : ""}`}
                                  onClick={(e) => { e.stopPropagation(); toggleFilter(colKey); }}
                                  title="Filter"
                                >
                                  <FilterFunnelIcon />
                                </button>
                              </div>
                            </th>
                          );
                        })}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={classes.trTotalSticky}>
                      {!hiddenCols.has("live") && columns.map((c) => (
                        <td key={`live-${c.key}`} className={classes.tdTotalNum} style={{ color: ACTUALS_C }}>{fmtVal(c.totalLive, c.isPct)}</td>
                      ))}
                      {!hiddenCols.has("comparison") && columns.map((c) => (
                        <td key={`comp-${c.key}`} className={classes.tdTotalNum} style={{ color: FORECAST_C }}>{fmtVal(c.totalComp, c.isPct)}</td>
                      ))}
                      {!hiddenCols.has("variance") && columns.map((c) => (
                        <td key={`var-${c.key}`} className={classes.tdTotalVar} style={{ color: varColor(c.totalVar) }}>{fmtVar(c.totalVar, c.isPct)}</td>
                      ))}
                    </tr>
                    {pagedContracts.map((row, idx) => {
                      const globalIdx = (safePage - 1) * pageSize + idx;
                      return (
                        <tr
                          key={row.contractId}
                          className={globalIdx % 2 === 1 ? classes.trStripe : ""}
                        >
                          {!hiddenCols.has("live") &&
                            effectiveKeys.map((key) => {
                              const def = rowDefs.find((r) => r.key === key);
                              return (
                                <td key={`live-${key}`} className={classes.tdLive}>
                                  {fmtVal(getValForKey(row.live, key), def?.isPct ?? false)}
                                </td>
                              );
                            })}
                          {!hiddenCols.has("comparison") &&
                            effectiveKeys.map((key) => {
                              const def = rowDefs.find((r) => r.key === key);
                              return (
                                <td key={`comp-${key}`} className={classes.tdComp}>
                                  {fmtVal(getValForKey(row.comparison, key), def?.isPct ?? false)}
                                </td>
                              );
                            })}
                          {!hiddenCols.has("variance") &&
                            effectiveKeys.map((key) => {
                              const def = rowDefs.find((r) => r.key === key);
                              const v = getValForKey(row.live, key) - getValForKey(row.comparison, key);
                              return (
                                <td key={`var-${key}`} className={classes.tdVar} style={{ color: varColor(v) }}>
                                  {fmtVar(v, def?.isPct ?? false)}
                                </td>
                              );
                            })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination bar */}
            {totalPages > 1 && (
              <div className={classes.paginationBar}>
                <div className={classes.paginationRight}>
                  <span className={classes.pageSizeLabel}>Rows Per Page</span>
                  <select
                    className={classes.pageSizeSelect}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={classes.paginationControls}>
                  <button
                    className={classes.pageBtn}
                    disabled={safePage === 1}
                    onClick={() => setPage(1)}
                    title="First Page"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="7,2 3,6 7,10" />
                      <polyline points="11,2 7,6 11,10" />
                    </svg>
                  </button>
                  <button
                    className={classes.pageBtn}
                    disabled={safePage === 1}
                    onClick={() => setPage((p) => p - 1)}
                    title="Previous Page"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="8,2 4,6 8,10" />
                    </svg>
                  </button>
                  {pageNumbers.map((p, i) => (
                    <React.Fragment key={p}>
                      {i > 0 && pageNumbers[i - 1] < p - 1 && (
                        <span className={classes.pageDots}>…</span>
                      )}
                      <button
                        className={`${classes.pageBtn} ${p === safePage ? classes.pageBtnActive : ""}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                  <button
                    className={classes.pageBtn}
                    disabled={safePage === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    title="Next Page"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="4,2 8,6 4,10" />
                    </svg>
                  </button>
                  <button
                    className={classes.pageBtn}
                    disabled={safePage === totalPages}
                    onClick={() => setPage(totalPages)}
                    title="Last Page"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="5,2 9,6 5,10" />
                      <polyline points="1,2 5,6 1,10" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Operator dropdown portal */}
      {openOpKey && createPortal(
        <OpDropdownContent
          activeOp={(colFilters[openOpKey]?.op) || "gt"}
          onSelect={(op) => selectOp(openOpKey, op)}
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        />,
        document.body
      )}
      {openFilterKey && createPortal(
        <div data-filter-panel className={classes.filterPanel} style={{ top: filterPanelPos.top, left: filterPanelPos.left }}>
          {renderFilterContent(openFilterKey)}
        </div>,
        document.body
      )}
    </div>
  );
}
